import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { ITransacao as Transacao } from '../types/firestore.types';

const TRANSACOES_COLLECTION = 'transacoes';

/**
 * Cria uma nova transação no sistema TugasCoins.
 * Esta função registra uma operação de débito ou crédito, associando-a a um cliente, unidade e operador.
 * Campos como 'id', 'criado_em' e 'atualizado_em' são gerados automaticamente.
 *
 * @param {Omit<Transacao, 'id' | 'criado_em' | 'atualizado_em'>} dadosTransacao - Os dados essenciais da transação a ser criada.
 * @returns {Promise<Transacao>} Uma promessa que resolve com o objeto da transação criada, incluindo seu ID e timestamps.
 * @throws {Error} Lança um erro se a operação de criação falhar, com uma mensagem amigável.
 */
export async function criarTransacao(
  dadosTransacao: Omit<Transacao, 'id' | 'criado_em' | 'atualizado_em'>
): Promise<Transacao> {
  // Defesa em profundidade, FORA do try — o catch abaixo troca a mensagem por
  // uma genérica, e aqui o motivo da recusa precisa chegar a quem chamou.
  // A barreira real são as regras do Firestore; isto é a segunda camada.
  // Débito com valor negativo vira crédito no cálculo do saldo, porque
  // useSaldo faz `saldo -= valor`.
  const valor = dadosTransacao.valor;
  if (typeof valor !== 'number' || !Number.isFinite(valor) || valor <= 0) {
    throw new Error('Valor da transação deve ser um número maior que zero.');
  }
  if (dadosTransacao.tipo_operacao !== 'debito' && dadosTransacao.tipo_operacao !== 'credito') {
    throw new Error('Tipo de operação inválido.');
  }

  try {
    const now = Timestamp.now();
    
    const transacaoParaSalvar = {
      ...dadosTransacao,
      data_hora: dadosTransacao.data_hora || now,
      criado_em: now,
      atualizado_em: now,
    };

    const docRef = await addDoc(collection(db, TRANSACOES_COLLECTION), transacaoParaSalvar);

    const novaTransacao: Transacao = {
      id: docRef.id,
      ...transacaoParaSalvar,
    };

    return novaTransacao;
  } catch (error) {
    console.error('Falha crítica ao criar transação:', error);
    throw new Error('Não foi possível registrar a transação. Verifique os dados e tente novamente.');
  }
}

/**
 * Lista todas as transações registradas no sistema TugasCoins.
 * As transações são retornadas ordenadas pela data e hora de criação em ordem decrescente,
 * proporcionando uma visão cronológica das operações mais recentes.
 *
 * @returns {Promise<Transacao[]>} Uma promessa que resolve com um array de objetos de transação.
 * @throws {Error} Lança um erro se a operação de listagem falhar.
 */
export async function listarTransacoes(): Promise<Transacao[]> {
  try {
    const q = query(collection(db, TRANSACOES_COLLECTION), orderBy('criado_em', 'desc'));
    const querySnapshot = await getDocs(q);
    const transacoes: Transacao[] = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    } as Transacao));
    return transacoes;
  } catch (error) {
    console.error('Falha crítica ao listar transações:', error);
    throw new Error('Não foi possível carregar as transações. Tente recarregar a página.');
  }
}

/**
 * Busca transações específicas de um cliente pelo seu ID único.
 * As transações são ordenadas pela data e hora da transação em ordem decrescente,
 * facilitando a visualização do histórico mais recente do cliente.
 *
 * @param {string} clienteId - O ID único do cliente para o qual as transações serão buscadas.
 * @returns {Promise<Transacao[]>} Uma promessa que resolve com um array de objetos de transação pertencentes ao cliente.
 * @throws {Error} Lança um erro se a operação de busca falhar.
 */
export async function buscarTransacoesPorClienteId(clienteId: string): Promise<Transacao[]> {
  try {
    const q = query(
      collection(db, TRANSACOES_COLLECTION),
      where('cliente_id', '==', clienteId),
      orderBy('data_hora', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const transacoes: Transacao[] = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    } as Transacao));
    return transacoes;
  } catch (error) {
    console.error(`Falha crítica ao buscar transações para o cliente ${clienteId}:`, error);
    throw new Error('Não foi possível buscar o histórico de transações do cliente. Tente novamente.');
  }
}

/**
 * Busca uma transação individual pelo seu ID único.
 *
 * @param {string} id - O ID único da transação a ser buscada.
 * @returns {Promise<Transacao | null>} Uma promessa que resolve com o objeto da transação encontrada ou `null` se não existir.
 * @throws {Error} Lança um erro se a operação de busca falhar.
 */
export async function buscarTransacaoPorId(id: string): Promise<Transacao | null> {
  try {
    const docRef = doc(db, TRANSACOES_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Transacao;
    } else {
      return null;
    }
  } catch (error) {
    console.error(`Falha crítica ao buscar transação com ID ${id}:`, error);
    throw new Error('Não foi possível encontrar a transação solicitada. Verifique o ID.');
  }
}

/**
 * Atualiza uma transação existente no sistema TugasCoins.
 * Apenas os campos fornecidos em `dadosAtualizados` serão modificados. O campo `atualizado_em` é atualizado automaticamente.
 *
 * @param {string} id - O ID único da transação a ser atualizada.
 * @param {Partial<Omit<Transacao, 'id' | 'criado_em'>>} dadosAtualizados - Um objeto contendo os campos da transação a serem atualizados.
 * @returns {Promise<void>} Uma promessa que resolve quando a transação é atualizada com sucesso.
 * @throws {Error} Lança um erro se a operação de atualização falhar.
 */
export async function atualizarTransacao(
  id: string,
  dadosAtualizados: Partial<Omit<Transacao, 'id' | 'criado_em'>>
): Promise<void> {
  try {
    const docRef = doc(db, TRANSACOES_COLLECTION, id);
    await updateDoc(docRef, {
      ...dadosAtualizados,
      atualizado_em: Timestamp.now(),
    });
  } catch (error) {
    console.error(`Falha crítica ao atualizar transação com ID ${id}:`, error);
    throw new Error('Não foi possível atualizar a transação. Verifique os dados e tente novamente.');
  }
}

/**
 * Exclui uma transação do sistema TugasCoins pelo seu ID único.
 *
 * @param {string} id - O ID único da transação a ser excluída.
 * @returns {Promise<void>} Uma promessa que resolve quando a transação é excluída com sucesso.
 * @throws {Error} Lança um erro se a operação de exclusão falhar.
 */
export async function excluirTransacao(id: string): Promise<void> {
  try {
    const docRef = doc(db, TRANSACOES_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error(`Falha crítica ao excluir transação com ID ${id}:`, error);
    throw new Error('Não foi possível excluir a transação. Tente novamente.');
  }
}

/**
 * Busca transações com filtros opcionais, permitindo uma pesquisa flexível.
 * É possível filtrar por tipo de operação, unidade, operador e um período de datas.
 * As transações são sempre ordenadas pela data e hora da transação em ordem decrescente.
 *
 * @param {object} filtros - Objeto contendo os critérios de filtro.
 * @param {'debito' | 'credito'} [filtros.tipoOperacao] - O tipo de operação desejado.
 * @param {string} [filtros.unidadeId] - O ID da unidade onde a transação ocorreu.
 * @param {string} [filtros.operadorId] - O ID do operador que realizou a transação.
 * @param {Timestamp} [filtros.dataInicio] - Data e hora de início do período para busca (inclusive).
 * @param {Timestamp} [filtros.dataFim] - Data e hora de fim do período para busca (inclusive).
 * @returns {Promise<Transacao[]>} Uma promessa que resolve com um array de objetos de transação filtrados.
 * @throws {Error} Lança um erro se a operação de busca com filtros falhar.
 */
export async function buscarTransacoesComFiltros({
  tipoOperacao,
  unidadeId,
  operadorId,
  dataInicio,
  dataFim,
}: {
  tipoOperacao?: 'debito' | 'credito';
  unidadeId?: string;
  operadorId?: string;
  dataInicio?: Timestamp;
  dataFim?: Timestamp;
}): Promise<Transacao[]> {
  try {
    let q = query(collection(db, TRANSACOES_COLLECTION));

    if (tipoOperacao) {
      q = query(q, where('tipo_operacao', '==', tipoOperacao));
    }
    if (unidadeId) {
      q = query(q, where('unidade_id', '==', unidadeId));
    }
    if (operadorId) {
      q = query(q, where('operador_id', '==', operadorId));
    }
    if (dataInicio) {
      q = query(q, where('data_hora', '>=', dataInicio));
    }
    if (dataFim) {
      q = query(q, where('data_hora', '<=', dataFim));
    }

    q = query(q, orderBy('data_hora', 'desc'));

    const querySnapshot = await getDocs(q);
    const transacoes: Transacao[] = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    } as Transacao));
    return transacoes;
  } catch (error) {
    console.error('Falha crítica ao buscar transações com filtros:', error);
    throw new Error('Não foi possível aplicar os filtros e buscar as transações. Tente novamente.');
  }
}

/**
 * Calcula o saldo total de TugasCoins para um cliente específico, somando créditos e subtraindo débitos.
 *
 * @param {string} clienteId - O ID único do cliente.
 * @returns {Promise<number>} Uma promessa que resolve com o saldo total atual do cliente.
 * @throws {Error} Lança um erro se a operação de cálculo de saldo falhar.
 */
export async function calcularSaldoCliente(clienteId: string): Promise<number> {
  try {
    const q = query(
      collection(db, TRANSACOES_COLLECTION),
      where('cliente_id', '==', clienteId)
    );
    const querySnapshot = await getDocs(q);
    let saldo = 0;
    querySnapshot.docs.forEach((doc) => {
      const transacao = doc.data() as Transacao;
      if (transacao.tipo_operacao === 'credito') {
        saldo += transacao.valor;
      } else if (transacao.tipo_operacao === 'debito') {
        saldo -= transacao.valor;
      }
    });
    return saldo;
  } catch (error) {
    console.error(`Falha crítica ao calcular saldo para o cliente ${clienteId}:`, error);
    throw new Error('Não foi possível calcular o saldo do cliente. Tente novamente.');
  }
}